import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import colors from '~/constants/colors';
import { useDirection } from '~/hooks/i18nHooks';

interface OtpInputProps {
  value?: string;
  onChange?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  name?: string;
  hasError?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  hasError,
  value = '',
  onChange,
  length = 5,
  disabled = false,
  name = 'otp',
}) => {
  const dir = useDirection();
  // Initialize with existing value or blanks
  const [otp, setOtp] = useState<string[]>(
    value.split('').concat(Array(length - value.length).fill(''))
  );

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  useEffect(() => {
    if (value) {
      const newOtp = value.split('').concat(Array(length - value.length).fill(''));
      setOtp(newOtp);
    }
  }, [value, length]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const raw = e.target.value;
    // Filter: keep only digits
    const digitOnly = raw.replace(/\D/g, '');
    if (digitOnly.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = digitOnly;
    setOtp(newOtp);

    if (onChange) {
      onChange(newOtp.join(''));
    }

    if (digitOnly && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Allow: digits, Backspace, arrows, Tab
    const isDigit = /^[0-9]$/.test(e.key);
    const isControlKey =
      e.key === 'Backspace' || e.key === 'Tab' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (!isDigit && !isControlKey) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);

        if (onChange) {
          onChange(newOtp.join(''));
        }

        inputRefs.current[index - 1]?.focus();
        // Prevent the default so the current box doesn’t get a leftover char
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    // Clean non-digits
    const cleaned = pastedData.replace(/\D/g, '');
    if (cleaned.length === 0) return;

    const pastedOtp = cleaned.slice(0, length).split('');
    const newOtp = Array(length).fill('');
    pastedOtp.forEach((digit, idx) => {
      newOtp[idx] = digit;
    });

    setOtp(newOtp);

    if (onChange) {
      onChange(newOtp.join(''));
    }

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <OtpContainer dir={dir}>
      <input type="hidden" name={name} value={otp.join('')} />
      {Array.from({ length }, (_, index) => (
        <OtpBox
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          value={otp[index]}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          maxLength={1}
          autoComplete="off"
          disabled={disabled}
          inputMode="numeric"
          pattern="[0-9]*"
          style={{
            borderColor: hasError ? '#F2A5B6' : undefined,
          }}
        />
      ))}
    </OtpContainer>
  );
};

const OtpContainer = styled.div<{ dir?: string }>`
  display: flex;
  gap: 12px; /* adjust spacing as needed */
  direction: ltr !important;
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-end' : 'flex-start')};
  margin-bottom: 10px;
  width: 100%;
`;

const OtpBox = styled.input`
  outline: none;
  width: 56px;
  height: 56px;
  font-size: 24px;
  text-align: center;
  border-radius: 8px;
  border: 1px solid #A5CDE9;
  direction: ltr !important;

  &:focus {
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

export default OtpInput;
